import { useForm, Controller } from 'react-hook-form';
import { Input } from '@tigergraph/app-ui-lib/input';
import { Button } from '@tigergraph/app-ui-lib/button';
import { styled, useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { ThemeProvider } from '@/contexts/themeContext.tsx';
import { FormControl } from './FormControl';
import { axiosOrgService } from '@/lib/network';
import { useMutation } from 'react-query';
import trackUtil from '@/utils/analytics-service';
import { AxiosError } from 'axios';
import { getErrorMessage } from '@/utils/utils';
import { useOrgContext } from '@/contexts/orgContext';
import { useNavigate } from 'react-router-dom';
import { useUserGuideContext } from '@/components/userguide/userGuideContext';
import { defaultOnboardingState, onboardingAtom } from '@/pages/onboarding/state';
import mediaQuery from '@/lib/breakpoints';
import { FaRegCheckCircle } from 'react-icons/fa';
import AntiFraudIcon from '@/pages/onboarding/icon/sample.svg?react';
import ImportDataIcon from '@/pages/onboarding/icon/own.svg?react';
import { GearSpinner } from '@tigergraph/app-ui-lib/spinner';
import { ErrorDisplay } from '@/components/error';
import toast from 'react-hot-toast';
import { useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { MdOutlineRocketLaunch } from 'react-icons/md';
import { HiOutlineUser } from 'react-icons/hi';
import Logo from '@/pages/home/logo';

type OnboardingChoice = 'fraud' | 'own';

type SurveyFormData = {
  firstName: string;
  lastName: string;
  company: string;
};

// --- Styled Components ---

const PageWrapper = styled('div', ({ $theme }) => ({
  minHeight: '100vh',
  height: '100%',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  alignItems: 'center',
  [mediaQuery.small]: {
    marginTop: '16px',
    gap: '16px',
  },
  backgroundColor: $theme.colors['background.primary'],
}));

const Card = styled('div', ({ $theme }) => ({
  backgroundColor: $theme.colors['background.primary'],
  borderRadius: '4px',
  padding: '64px 0',
  width: 'min(90%, 720px)',
  [mediaQuery.small]: {
    padding: '16px',
  },
}));

const Title = styled('h1', {
  fontFamily: 'Urbanist',
  fontSize: '36px',
  fontWeight: 700,
  lineHeight: '40px',
  textAlign: 'center',
  marginBottom: '8px',
});

const Subtitle = styled('p', ({ $theme }) => ({
  fontSize: '14px',
  color: $theme.colors['text.secondary'],
  textAlign: 'center',
  marginBottom: '36px',
}));

const SectionHeader = styled('div', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '16px',
  fontWeight: 600,
  color: $theme.colors.contentPrimary,
  marginBottom: '16px',
  paddingBottom: '8px',
  borderBottom: `1px solid ${$theme.colors['border.tertiary']}`,
}));

const NameRow = styled('div', {
  display: 'flex',
  gap: '16px',
  [mediaQuery.small]: {
    flexDirection: 'column',
    gap: '0',
  },
});

const CardsRow = styled('div', {
  display: 'flex',
  gap: '16px',
  marginTop: '8px',
  [mediaQuery.small]: {
    flexDirection: 'column',
  },
});

type OptionCardProps = { $selected: boolean };

const OptionCard = styled<'div', OptionCardProps>('div', ({ $selected, $theme }) => ({
  flex: 1,
  borderRadius: '12px',
  padding: '20px',
  cursor: 'pointer',
  border: $selected ? `2px solid ${$theme.colors['border.selected']}` : `2px solid ${$theme.colors['border.tertiary']}`,
  background: $theme.colors['background.secondary'],
  transition: 'border-color 0.15s, background 0.15s',
  position: 'relative',
  ':hover': {
    borderColor: $theme.colors['border.selected'],
  },
}));

type CardIconProps = { $selected: boolean };

const CardIcon = styled<'div', CardIconProps>('div', ({ $selected, $theme }) => ({
  fontSize: '28px',
  color: $selected ? $theme.colors['background.selected.bold'] : $theme.colors['text.secondary'],
  width: '48px',
  height: '48px',
  borderRadius: '8px',
  background: $selected ? $theme.colors['background.selected.subtlest'] : $theme.colors['background.tertiary'],
  marginBottom: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'color 0.15s, background 0.15s',
}));

const CardTitle = styled('div', ({ $theme }) => ({
  fontSize: '18px',
  fontWeight: 700,
  marginBottom: '6px',
  color: $theme.colors.contentPrimary,
}));

const CardDesc = styled('div', ({ $theme }) => ({
  fontSize: '13px',
  color: $theme.colors['text.secondary'],
  marginBottom: '12px',
  lineHeight: '1.5',
}));

const CardList = styled('ul', {
  padding: 0,
  margin: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

type CardListItemProps = { $selected: boolean };

const CardListItem = styled<'li', CardListItemProps>('li', ({ $selected, $theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  fontSize: '13px',
  gap: '8px',
  color: $selected ? $theme.colors.contentPrimary : $theme.colors['text.secondary'],
}));

const Badge = styled('div', ({ $theme }) => ({
  position: 'absolute',
  top: '-12px',
  right: '5%',
  background: $theme.colors['background.selected.bold'],
  color: $theme.colors['text.inverse'],
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.05em',
  padding: '2px 10px',
  borderRadius: '2px',
  whiteSpace: 'nowrap',
}));

const FooterText = styled('p', ({ $theme }) => ({
  fontSize: '12px',
  color: $theme.colors['text.tertiary'],
  textAlign: 'center',
  marginTop: '12px',
}));

export default function GetStarted() {
  const [css, theme] = useStyletron();
  const { rawOrg, setRawOrg } = useOrgContext();
  const navigate = useNavigate();
  const { acquiredWorkspace, workspaceError, startAcquireWorkgroup } = useUserGuideContext();
  const [, setOnboardingState] = useAtom(onboardingAtom);
  const [selectedChoice, setSelectedChoice] = useState<OnboardingChoice>('fraud');
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const hasStartedAcquireRef = useRef(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SurveyFormData>();

  // Once workspace is acquired, navigate based on the selected choice
  useEffect(() => {
    if (!isSettingUp || !acquiredWorkspace) return;
    setOnboardingState({
      ...defaultOnboardingState,
      workspace: acquiredWorkspace,
    });
    if (selectedChoice === 'fraud') {
      navigate(`/explore?workspace_id=${acquiredWorkspace.workspace_id}`, { replace: true });
    } else {
      navigate(`/onboarding/import-csv?workspace_id=${acquiredWorkspace.workspace_id}`, { replace: true });
    }
  }, [acquiredWorkspace, isSettingUp, selectedChoice, navigate, setOnboardingState]);

  // Countdown redirect on workspace error
  useEffect(() => {
    if (!isSettingUp || !workspaceError) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/', { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [workspaceError, isSettingUp, navigate]);

  const surveyMutation = useMutation(
    async (data: SurveyFormData) => {
      const survey = {
        first_name: data.firstName,
        last_name: data.lastName,
        company: data.company,
        role: '',
        use_for: '',
        biz_use_case: [''],
      };
      await submitSurvey(survey);
      await updateOrg(rawOrg?.org_id, true);
    },
    {
      onSuccess: () => {
        trackUtil.trackFinishSurveyForm('Onboard', 'Success');
        setRawOrg({
          ...rawOrg,
          marketing_done: true,
        });
      },
      onError: (err: AxiosError) => {
        trackUtil.trackFinishSurveyForm('Onboard', 'Fail');
        toast.error(`Failed to save the data.\n${getErrorMessage(err)}`);
      },
    }
  );

  const handleContinue = handleSubmit(async (data) => {
    try {
      await surveyMutation.mutateAsync(data);
      // Guard against double-triggering if user clicks twice
      if (hasStartedAcquireRef.current) return;
      hasStartedAcquireRef.current = true;
      setIsSettingUp(true);
      startAcquireWorkgroup();
    } catch {
      // surveyMutation.onError handles the toast
    }
  });

  const isSurveyLoading = surveyMutation.isLoading;
  const fraudSelected = selectedChoice === 'fraud';
  const ownSelected = selectedChoice === 'own';
  const iconColor = theme.colors['background.selected.bold'];

  // Show workspace setup screen after survey is submitted
  if (isSettingUp) {
    return (
      <ThemeProvider>
        <div
          className={css({
            minHeight: '100vh',
            width: '100vw',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <div
            className={css({
              width: '500px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            })}
          >
            {workspaceError ? (
              <div>
                <div className={css({ marginTop: '32px', fontSize: '22px', fontWeight: 550 })}>
                  Failed to set up your workspace
                </div>
                <ErrorDisplay error={new Error(workspaceError)} />
                <div
                  className={css({
                    marginTop: '16px',
                    maxWidth: '500px',
                    fontSize: '14px',
                    textAlign: 'center',
                    fontWeight: 300,
                  })}
                >
                  Redirecting to home page in {countdown} seconds...{' '}
                  <Button kind="link" size="compact" onClick={() => navigate('/')}>
                    Go to Home
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <GearSpinner size="large" />
                <div className={css({ marginTop: '32px', fontSize: '22px', fontWeight: 550 })}>
                  Setting Up Your Workspace...
                </div>
                <div
                  className={css({
                    marginTop: '16px',
                    maxWidth: '500px',
                    fontSize: '14px',
                    textAlign: 'center',
                    fontWeight: 300,
                  })}
                >
                  Thank you for registering. We are currently provisioning your workspace, which will take just a few
                  seconds. Please hold on while we set everything up for you.
                </div>
              </>
            )}
          </div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <PageWrapper>
        <Card>
          <div
            className={css({
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '48px',
              transform: 'scale(2)',
              transformOrigin: 'center',
            })}
          >
            <Logo alwaysShow={true} />
          </div>
          <Title>Get started with TigerGraph Savanna</Title>
          <Subtitle>Set up your workspace and choose your exploration path.</Subtitle>
          <form>
            {/* Basic Information */}
            <SectionHeader>
              <HiOutlineUser size={18} color={theme.colors['background.selected.bold']} />
              Basic Information
            </SectionHeader>
            <NameRow>
              <div className={css({ flex: 1 })}>
                <FormControl label="First Name" error={errors?.firstName?.message}>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: 'Please enter your first name.' }}
                    render={({ field }) => <Input {...field} placeholder="Jane" size="large" />}
                  />
                </FormControl>
              </div>
              <div className={css({ flex: 1 })}>
                <FormControl label="Last Name" error={errors?.lastName?.message}>
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: 'Please enter your last name.' }}
                    render={({ field }) => <Input {...field} placeholder="Doe" size="large" />}
                  />
                </FormControl>
              </div>
            </NameRow>
            <FormControl label="Your Company" error={errors?.company?.message}>
              <Controller
                name="company"
                control={control}
                rules={{ required: 'Please enter your company name.' }}
                render={({ field }) => (
                  <Input {...field} placeholder="Enter the name of your company or organization" size="large" />
                )}
              />
            </FormControl>

            {/* Onboarding Path Selection */}
            <div className={css({ marginTop: '28px' })}>
              <SectionHeader>
                <MdOutlineRocketLaunch size={18} color={theme.colors['background.selected.bold']} />
                How would you like to get started?
              </SectionHeader>
              <CardsRow>
                {/* Fraud Detection Card */}
                <OptionCard $selected={fraudSelected} onClick={() => setSelectedChoice('fraud')}>
                  <Badge>RECOMMENDED FOR FIRST-TIME USERS</Badge>
                  <div className={css({ display: 'flex', alignItems: 'center', gap: '12px' })}>
                    <CardIcon $selected={fraudSelected}>
                      <AntiFraudIcon />
                    </CardIcon>
                    <CardTitle>Explore with Pre-Loaded Fraud Detection Solution</CardTitle>
                  </div>
                  <CardDesc>
                    New to graph? Get started instantly with a fully pre-built fraud detection graph including schema,
                    pre-loaded data, queries, and dashboards.
                  </CardDesc>
                  <CardList>
                    <CardListItem $selected={fraudSelected}>
                      <FaRegCheckCircle
                        size={14}
                        color={fraudSelected ? iconColor : theme.colors['text.tertiary']}
                        style={{ flexShrink: 0, marginTop: '2px' }}
                      />
                      Explore fraud detection use cases immediately
                    </CardListItem>
                    <CardListItem $selected={fraudSelected}>
                      <FaRegCheckCircle
                        size={14}
                        color={fraudSelected ? iconColor : theme.colors['text.tertiary']}
                        style={{ flexShrink: 0, marginTop: '2px' }}
                      />
                      Includes sample data and step-by-step documentation
                    </CardListItem>
                    <CardListItem $selected={fraudSelected}>
                      <FaRegCheckCircle
                        size={14}
                        color={fraudSelected ? iconColor : theme.colors['text.tertiary']}
                        style={{ flexShrink: 0, marginTop: '2px' }}
                      />
                      Run queries out of the box
                    </CardListItem>
                    <CardListItem $selected={fraudSelected}>
                      <FaRegCheckCircle
                        size={14}
                        color={fraudSelected ? iconColor : theme.colors['text.tertiary']}
                        style={{ flexShrink: 0, marginTop: '2px' }}
                      />
                      No setup required
                    </CardListItem>
                  </CardList>
                </OptionCard>

                {/* Own Data Card */}
                <OptionCard $selected={ownSelected} onClick={() => setSelectedChoice('own')}>
                  <div className={css({ display: 'flex', alignItems: 'center', gap: '12px' })}>
                    <CardIcon $selected={ownSelected}>
                      <ImportDataIcon />
                    </CardIcon>
                    <CardTitle>Explore with Your Own Data</CardTitle>
                  </div>
                  <CardDesc>
                    Already have a graph use case? Import your data and build your graph from scratch in minutes.
                  </CardDesc>
                  <CardList>
                    <CardListItem $selected={ownSelected}>
                      <FaRegCheckCircle
                        size={14}
                        color={ownSelected ? iconColor : theme.colors['text.tertiary']}
                        style={{ flexShrink: 0, marginTop: '2px' }}
                      />
                      Use BuildAI to automatically model your graph and get started quickly
                    </CardListItem>
                    <CardListItem $selected={ownSelected}>
                      <FaRegCheckCircle
                        size={14}
                        color={ownSelected ? iconColor : theme.colors['text.tertiary']}
                        style={{ flexShrink: 0, marginTop: '2px' }}
                      />
                      Discover patterns and relationships in your data
                    </CardListItem>
                    <CardListItem $selected={ownSelected}>
                      <FaRegCheckCircle
                        size={14}
                        color={ownSelected ? iconColor : theme.colors['text.tertiary']}
                        style={{ flexShrink: 0, marginTop: '2px' }}
                      />
                      Leverage our graph algorithm library for faster insights
                    </CardListItem>
                    <CardListItem $selected={ownSelected}>
                      <FaRegCheckCircle
                        size={14}
                        color={ownSelected ? iconColor : theme.colors['text.tertiary']}
                        style={{ flexShrink: 0, marginTop: '2px' }}
                      />
                      Run graph traversals with an easy-to-use, no-code UI
                    </CardListItem>
                  </CardList>
                </OptionCard>
              </CardsRow>
            </div>

            {/* Actions */}
            <Button
              type="button"
              size="large"
              isLoading={isSurveyLoading}
              disabled={isSurveyLoading}
              onClick={handleContinue}
              overrides={{
                BaseButton: {
                  style: {
                    width: '100%',
                    display: 'block',
                    marginTop: '32px',
                    height: '40px',
                  },
                },
              }}
            >
              Continue
            </Button>
            <FooterText>
              By continuing, you agree to TigerGraph&apos;s{' '}
              <a
                href="https://www.tigergraph.com/terms-of-service/"
                target="_blank"
                rel="noreferrer"
                className={css({ color: theme.colors['text.link'], textDecoration: 'none' })}
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="https://www.tigergraph.com/privacy-policy/"
                target="_blank"
                rel="noreferrer"
                className={css({ color: theme.colors['text.link'], textDecoration: 'none' })}
              >
                Privacy Policy
              </a>
              .
            </FooterText>
          </form>
        </Card>
      </PageWrapper>
    </ThemeProvider>
  );
}

async function submitSurvey(data: {
  first_name: string;
  last_name: string;
  company: string;
  role: string;
  use_for: string;
  biz_use_case: string[];
}) {
  return await axiosOrgService.post(`/orgs/survey`, {
    ...data,
    savanna: true,
  });
}

async function updateOrg(orgId: string, marketing_done: boolean) {
  return await axiosOrgService.put(`/orgs/${orgId}`, {
    marketing_done,
  });
}
